/**
 * 成本計算引擎 V2 — 動態工段鏈
 *
 * WOVEN: 胚布(YD) → [動態工段] → 總成本
 * KNIT:  紗線(KG) → [KG工段] → KG→YD轉換 → [YD工段] → 總成本
 *
 * 工段計算規則:
 *   一般: subtotal = subtotal/(1-損耗%) + 費用 + 驗貨
 *   藥劑: subtotal += 費用/(1-損耗%)  (損耗只加在自己的費用上)
 *
 * 售價 = 總成本 / (1-費用%) / (1-毛利%-佣金%) / 匯率
 */
import type {
  FabricConfig, FabricResult, FullResult,
  StageResult, ConversionResult, ChainStage,
} from './types';
import { KNIT_KG_STAGES, STAGE_LABELS } from './types';

// ---------- 單一工段計算 ----------
function processStage(
  sub: number,
  stage: ChainStage,
  unit: 'KG' | 'YD',
): { result: StageResult; subtotal: number } {
  const lr = stage.lossRate / 100;

  if (stage.type === 'inspection') {
    // 驗貨：直接加費用，無損耗
    sub += stage.cost;
    return {
      result: {
        id: stage.id,
        name: STAGE_LABELS[stage.type],
        stageCost: stage.cost,
        lossAmount: 0,
        inspectionFee: 0,
        subtotal: sub,
        unit,
      },
      subtotal: sub,
    };
  }

  if (stage.type === 'chemical') {
    // 藥劑：損耗只加在自己的費用上
    const adjusted = lr > 0 && lr < 1 ? stage.cost / (1 - lr) : stage.cost;
    const lossAmt = adjusted - stage.cost;
    sub += adjusted;
    return {
      result: {
        id: stage.id,
        name: STAGE_LABELS[stage.type],
        stageCost: stage.cost,
        lossAmount: lossAmt,
        inspectionFee: 0,
        subtotal: sub,
        unit,
      },
      subtotal: sub,
    };
  }

  // 一般工段：損耗加在累計成本上
  const before = sub;
  if (lr > 0 && lr < 1) sub = sub / (1 - lr);
  const lossAmt = sub - before;
  const inspFee = stage.inspectionFee;
  sub += stage.cost + inspFee;

  return {
    result: {
      id: stage.id,
      name: STAGE_LABELS[stage.type],
      stageCost: stage.cost,
      lossAmount: lossAmt,
      inspectionFee: inspFee,
      subtotal: sub,
      unit,
    },
    subtotal: sub,
  };
}

// ---------- 單一布料計算 ----------
function calculateFabric(config: FabricConfig): FabricResult {
  const stages: StageResult[] = [];
  let sub = 0;

  if (config.type === 'woven') {
    // WOVEN 基礎：(胚價/(1-損耗)) + 加工費
    const lr = config.baseLossRate / 100;
    const base = lr > 0 && lr < 1
      ? config.basePrice / (1 - lr)
      : config.basePrice;
    sub = base + config.processingFee;
    stages.push({
      id: 'base',
      name: '胚布',
      stageCost: config.basePrice + config.processingFee,
      lossAmount: base - config.basePrice,
      inspectionFee: 0,
      subtotal: sub,
      unit: 'YD',
    });

    // 所有工段都是 YD
    for (const stage of config.stages) {
      const r = processStage(sub, stage, 'YD');
      stages.push(r.result);
      sub = r.subtotal;
    }

    return { stages, totalCostYd: sub };

  } else {
    // KNIT 基礎：紗價 = Σ(price × ratio%)
    const yarnCost = config.yarns.reduce((s, y) => s + y.price * (y.ratio / 100), 0);
    sub = yarnCost;
    if (config.yarns.length > 0) {
      stages.push({
        id: 'base',
        name: '紗線配比',
        stageCost: yarnCost,
        lossAmount: 0,
        inspectionFee: 0,
        subtotal: sub,
        unit: 'KG',
      });
    }

    // 依序處理工段，遇到第一個 YD 工段時自動插入 KG→YD 轉換
    let converted = false;
    let conversion: ConversionResult | undefined;

    const doConversion = () => {
      const kgPerYd = config.fabricWeight / 1000;
      const costPerKg = sub;
      const costPerYd = costPerKg * kgPerYd;
      // 驗貨費已移至獨立「驗貨」工段，轉換只做單位換算
      sub = costPerYd;
      conversion = { costPerKg, costPerYd, subtotal: sub };
      converted = true;
    };

    for (const stage of config.stages) {
      const isYd = !KNIT_KG_STAGES.includes(stage.type);

      // 第一個 YD 工段 → 先轉換
      if (isYd && !converted) doConversion();

      // KNIT 驗貨：費用依碼重自動計算
      let effectiveStage = stage;
      if (stage.type === 'inspection') {
        const kgPerYd = config.fabricWeight / 1000;
        const autoCost = config.fabricWeight >= 250 ? 5 * kgPerYd : 1.5;
        effectiveStage = { ...stage, cost: autoCost };
      }

      const unit = converted ? 'YD' : 'KG';
      const r = processStage(sub, effectiveStage, unit);
      stages.push(r.result);
      sub = r.subtotal;
    }

    // 全部都是 KG 工段，最後仍需轉換
    if (!converted) doConversion();

    return { stages, conversion, totalCostYd: sub };
  }
}

// ---------- 貼合計算 helper ----------
function applyLamination(
  costA: number, costB: number,
  lamCost: number, lamCost2: number, lamLossRate: number,
): number {
  const layerSum = costA + costB;
  const lr = lamLossRate / 100;
  const afterLoss = lr > 0 && lr < 1 ? layerSum / (1 - lr) : layerSum;
  return afterLoss + lamCost + lamCost2;
}

// ---------- 總計算（含雙層/4層貼合 + 後續工段 + 報價） ----------
export interface CalculateParams {
  fabricCount: 1 | 2 | 3;
  fabric1: FabricConfig;
  fabric2?: FabricConfig;
  fabric3?: FabricConfig;
  lam1Cost: number;       // 第一次貼合費
  lam1Cost2: number;
  lam1LossRate: number;
  postLam1Stages: ChainStage[];  // 第一次貼合後工段（4層：雙層→三層之間）
  lam2Cost: number;       // 第二次貼合費（4層用）
  lam2Cost2: number;
  lam2LossRate: number;
  postLamStages: ChainStage[];   // 最終貼合後工段
  expenseRate: number;
  profitMargin: number;
  commissionRate: number;
  exchangeRate: number;
}

export function calculate(p: CalculateParams): FullResult {
  const f1 = calculateFabric(p.fabric1);
  let f2: FabricResult | undefined;
  let f3: FabricResult | undefined;
  let combined = f1.totalCostYd;
  let lam1Cost = combined;
  let lam2Cost = combined;

  const postLam1Results: StageResult[] = [];

  // 第一次貼合 (L1 + L2)
  if (p.fabricCount >= 2 && p.fabric2) {
    f2 = calculateFabric(p.fabric2);
    combined = applyLamination(f1.totalCostYd, f2.totalCostYd,
      p.lam1Cost, p.lam1Cost2, p.lam1LossRate);
    lam1Cost = combined;

    // 第一次貼合後工段（4層模式：雙層→三層之間）
    for (const stage of p.postLam1Stages) {
      const r = processStage(combined, stage, 'YD');
      postLam1Results.push(r.result);
      combined = r.subtotal;
    }
  }

  const afterPostLam1Cost = combined;

  // 第二次貼合 (上次結果 + L3)
  if (p.fabricCount >= 3 && p.fabric3) {
    f3 = calculateFabric(p.fabric3);
    combined = applyLamination(combined, f3.totalCostYd,
      p.lam2Cost, p.lam2Cost2, p.lam2LossRate);
    lam2Cost = combined;
  }

  const laminatedCost = combined;

  // 最終貼合後工段（梭織規則，全 YD）
  const postLamResults: StageResult[] = [];
  if (p.fabricCount >= 2) {
    for (const stage of p.postLamStages) {
      const r = processStage(combined, stage, 'YD');
      postLamResults.push(r.result);
      combined = r.subtotal;
    }
  }

  const exp = p.expenseRate / 100;
  const prof = p.profitMargin / 100;
  const comm = p.commissionRate / 100;
  const denom = (1 - exp) * (1 - prof - comm) * p.exchangeRate;
  const selling = denom > 0 ? combined / denom : 0;
  // 小數點後第三位四捨五入（到分）
  const rounded = Math.round(selling * 100) / 100;

  return {
    fabric1: f1,
    fabric2: f2,
    fabric3: f3,
    lam1Cost,
    postLam1Results,
    afterPostLam1Cost,
    lam2Cost,
    laminatedCost,
    postLamResults,
    combinedCost: combined,
    sellingPrice: selling,
    roundedPrice: rounded,
  };
}
